/**
 * Tables primitive schemas — Sprint 3.
 *
 * Zod source-of-truth for the column-definition format that workflow
 * authors declare when creating a Table, and the API request/response
 * shapes for tables.* operations.
 *
 * See docs/STATEFUL-WORKFLOW-PLATFORM-SPEC-v1.1-AMENDMENTS.md §7 + §C.
 * CLAUDE.md Rule 26: Zod is source of truth.
 */

import { z } from 'zod';
import { ExportControlSchema, SensitivitySchema } from './document.schema.js';

// ── Classification (re-declared to avoid circular deps) ──────────────────
const ClassificationSchema = z
  .object({
    sensitivity: SensitivitySchema,
    exportControl: ExportControlSchema,
  })
  .strict();

// ── Column definition (recursive via z.lazy for nested objects) ──────────

export const COLUMN_TYPES = [
  'string',
  'number',
  'boolean',
  'date',
  'datetime',
  'email',
  'uuid',
  'jsonb',
  'enum',
  'array',
  'object',
] as const;
export type ColumnType = (typeof COLUMN_TYPES)[number];

interface ColumnDefBase {
  name: string;
  type: ColumnType;
  enum_values?: string[];
  required: boolean;
  default?: unknown;
  classification?: { sensitivity: string; exportControl: string };
  indexed?: boolean;
  description?: string;
}

export interface ColumnDef extends ColumnDefBase {
  array_item_type?: ColumnDef;
  object_schema?: ColumnDef[];
}

export const ColumnDefSchema: z.ZodType<ColumnDef> = z.lazy(() =>
  z.object({
    name: z
      .string()
      .min(1)
      .max(120)
      .regex(
        /^[a-z][a-z0-9_]*$/,
        'must be lowercase snake_case starting with a letter',
      ),
    type: z.enum(COLUMN_TYPES),
    enum_values: z.array(z.string()).optional(),
    array_item_type: z.lazy(() => ColumnDefSchema).optional(),
    object_schema: z.array(z.lazy(() => ColumnDefSchema)).optional(),
    required: z.boolean(),
    default: z.unknown().optional(),
    classification: ClassificationSchema.optional(),
    indexed: z.boolean().default(false),
    description: z.string().max(500).optional(),
  }),
);

// ── Table-level policies + index hints ───────────────────────────────────
export const TablePoliciesSchema = z
  .object({
    auto_compact_after_days: z.number().int().min(30).optional(),
    soft_delete_only: z.boolean().default(false),
  })
  .strict()
  .optional();

// ── Full table schema declaration ────────────────────────────────────────
export const TableSchemaSchema = z
  .object({
    columns: z.array(ColumnDefSchema).min(1),
    policies: TablePoliciesSchema,
  })
  .strict();

export type TableSchema = z.infer<typeof TableSchemaSchema>;

// ── MCP tool input schemas (Sprint 3) ────────────────────────────────────

export const TablesQueryInputSchema = z
  .object({
    table_slug: z.string().min(1).max(120),
    filter: z.record(z.string(), z.unknown()).optional(),
    order_by: z
      .object({
        field: z.string().min(1).max(120),
        dir: z.enum(['asc', 'desc']),
      })
      .optional(),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().min(0).default(0),
  })
  .strict();
export type TablesQueryInput = z.infer<typeof TablesQueryInputSchema>;

export const TablesUpsertInputSchema = z
  .object({
    table_slug: z.string().min(1).max(120),
    row_key: z.string().min(1).max(255),
    data: z.record(z.string(), z.unknown()),
    classification: ClassificationSchema.optional(),
    on_conflict: z.enum(['merge', 'replace', 'skip']).default('merge'),
  })
  .strict();
export type TablesUpsertInput = z.infer<typeof TablesUpsertInputSchema>;

export const TablesDeleteInputSchema = z
  .object({
    table_slug: z.string().min(1).max(120),
    row_key: z.string().min(1).max(255),
  })
  .strict();
export type TablesDeleteInput = z.infer<typeof TablesDeleteInputSchema>;

// ── Workflow node configs ────────────────────────────────────────────────

export const DbUpsertNodeConfigSchema = z
  .object({
    table_slug: z.string().min(1).max(120),
    row_key_template: z.string().min(1).max(500),
    data_template: z.record(z.string(), z.string()),
    classification_template: z.string().optional(),
    on_conflict: z.enum(['merge', 'replace', 'skip']).default('merge'),
  })
  .strict();
export type DbUpsertNodeConfig = z.infer<typeof DbUpsertNodeConfigSchema>;

export const DbQueryNodeConfigSchema = z
  .object({
    table_slug: z.string().min(1).max(120),
    filter_jsonb: z.record(z.string(), z.unknown()).default({}),
    limit: z.number().int().min(1).max(200).default(50),
    order_by: z
      .object({
        field: z.string().min(1).max(120),
        dir: z.enum(['asc', 'desc']),
      })
      .optional(),
  })
  .strict();
export type DbQueryNodeConfig = z.infer<typeof DbQueryNodeConfigSchema>;

export const DbUserStateNodeConfigSchema = z
  .object({
    table_slug: z.string().min(1).max(120),
    row_key_template: z.string().min(1).max(500),
    state_template: z.record(z.string(), z.string()),
  })
  .strict();
export type DbUserStateNodeConfig = z.infer<typeof DbUserStateNodeConfigSchema>;
